import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import type {
  CounselorMeritBadge,
  MeritBadge,
  MeritBadgeCounselorProfile,
  RosterYouthMember,
} from '@appTypes/esb';

export type ValidationOK = {
  error?: undefined;
  valid: true;
  youth: RosterYouthMember;
};

export type ValidationResult = ValidationOK | ValidationError;
export type Validator = (...args: unknown[]) => ValidationResult;

interface ValidationError {
  valid: false;
  youth: RosterYouthMember;
  error: string;
}

interface BulkAssignState {
  validationErrors: Record<number, ValidationError[]>;
  selectedYouth: RosterYouthMember[];
  counselorMB?: CounselorMeritBadge;
  mb?: MeritBadge;
  mbcProfile?: MeritBadgeCounselorProfile;
}

const initialState: BulkAssignState = {
  counselorMB: undefined,
  mb: undefined,
  validationErrors: {},
  selectedYouth: [],
};

export const bulkAssignSlice = createSlice({
  name: 'mbcBulkAssign',
  initialState,
  reducers: {
    resetState() {
      return initialState;
    },
    resetAfterDone(state) {
      // Only reset selected youth and validations
      state.selectedYouth = [];
      state.validationErrors = {};
    },
    setMBCProfile(
      state,
      action: PayloadAction<{ mbcProfile?: MeritBadgeCounselorProfile }>,
    ) {
      state.mbcProfile = action.payload.mbcProfile;
    },
    toggleYouth(
      state,
      action: PayloadAction<{
        youth: RosterYouthMember;
        checked: boolean;
      }>,
    ) {
      const { youth, checked } = action.payload;
      if (checked) {
        state.selectedYouth.push(youth);
      } else {
        state.selectedYouth = state.selectedYouth.filter(
          item => item.memberId !== youth.memberId,
        );
      }
    },
    setSelection(
      state,
      action: PayloadAction<{
        counselorMB: CounselorMeritBadge;
        mb: MeritBadge;
      }>,
    ) {
      // Reset any previous validations when resetting the selected merit badge
      state.validationErrors = initialState.validationErrors;
      state.mb = action.payload.mb;
      state.counselorMB = action.payload.counselorMB;
    },
    setValidationResults(
      state,
      action: PayloadAction<{
        validationErrors: BulkAssignState['validationErrors'];
      }>,
    ) {
      state.validationErrors = action.payload.validationErrors;
    },
  },
});

export const bulkAssignActions = bulkAssignSlice.actions;
